import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useQueryState } from 'nuqs'
import { DataTable } from 'mantine-datatable'
import { parseAsType } from '@util'
import { isDefined, isExportId, isNotEmpty, SECOND, type ExportData, type ExportId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useDisclosure } from '@mantine/hooks'
import { Modal } from '@mantine/core'
import { toReadableExportType, useExports, useExportCache, useTranslation, translateTitles } from '@front/model'
import { ButtonAdd, ButtonDocs, NotReady, Page } from '@components'
import { ImportStatusChip } from '../integration.import_.$importId/StatusChip'
import { ExportModal } from './ExportModal'
import { ExportDownloadButton } from './ExportDownloadButton'
import { ExportDetailsModal } from './ExportDetailsModal'

const parseAsExportId = parseAsType(isExportId)

const COLUMNS_BASE = [
  {
    accessor: 'name',
    title: 'Name',
    render: ({ name, exportId }: ExportData) => (isNotEmpty(name) ? name : exportId),
  },
  { accessor: 'type', title: 'Type', render: ({ type }: ExportData) => toReadableExportType(type) },
  { accessor: 'format', title: 'Format', render: ({ format }: ExportData) => format.toUpperCase() },
  { accessor: 'total', title: 'Total Rows', render: ({ total }: ExportData) => total.toLocaleString() },
  {
    accessor: 'processed',
    title: 'Progress',
    render: ({ processed, total }: ExportData) => {
      if (total === 0) return '100%'
      const percentage = Math.min(Math.round((processed / total) * 100), 100)
      return `${percentage}%`
    },
  },
  { accessor: 'status', title: 'Status', render: ({ status }: ExportData) => <ImportStatusChip status={status} /> },
  {
    accessor: 'actions',
    title: 'Actions',
    render: ({ exportId, status }: ExportData) =>
      status === 'Completed' ? <ExportDownloadButton exportId={exportId} /> : null,
  },
]

const POLL_INTERVAL = 2 * SECOND

function Export(): ReactElement {
  const { t } = useTranslation()
  const [shouldPoll, setShouldPoll] = useState(false)
  const { data, isLoading, error, isFetching, refetch } = useExports({
    refetchInterval: shouldPoll ? POLL_INTERVAL : undefined,
  })
  useEffect(() => {
    const hasRunningExports = data?.some((e) => e.status === 'New' || e.status === 'Processing')
    setShouldPoll(Boolean(hasRunningExports))
  }, [data])
  const cache = useExportCache()
  const [opened, { open, close }] = useDisclosure(false)
  const [selectedExportId, setSelectedExportId] = useQueryState('exportId', parseAsExportId)

  const columns = useMemo(() => translateTitles(COLUMNS_BASE, t), [t])

  const handleRefetch = (): void => {
    refetch()
    cache.clearList()
  }

  const handleExportCreated = (exportId: ExportId): void => {
    handleRefetch()
    setSelectedExportId(exportId)
  }

  const selectedExport = data?.find((e) => e.exportId === selectedExportId)

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Exports' isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={handleRefetch} title={t('Exports')}>
        <ButtonDocs path='export' />
        <ButtonAdd onClick={open}>{t('Export Data')}</ButtonAdd>
      </Page.Heading>
      <Page.Content>
        <DataTable
          idAccessor='exportId'
          records={data}
          style={{ minHeight: '200px' }}
          columns={columns}
          onRowClick={({ record }) => setSelectedExportId(record.exportId)}
        />
        <ExportModal opened={opened} onClose={close} onSuccess={handleExportCreated} />
        <Modal
          centered
          size='xl'
          opened={isDefined(selectedExportId)}
          onClose={() => setSelectedExportId(null)}
          title={t('Export Details')}
        >
          {selectedExport && <ExportDetailsModal exportData={selectedExport} />}
        </Modal>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/integration/export/')({
  component: Export,
})
